import CapitalizationUtil from "../../utils/CapitalizationUtil.ts";
import {
  AgentReportData,
  BankReportData,
  EmptyDocumentObject,
  NewDocumentObject,
  NewPolicyDocumentObject,
  PoliciesFormState,
  PolicyDetailsData,
  PolicyFullObject,
  PolicyValuesObject,
  ReportData,
} from "../../types/insurance.ts";
import {
  normalizeAnnexValues,
  normalizeDate,
  normalizePolicyFullProperties,
  normalizePolicySaveProperties,
  normalizePolicyValues,
  revertNormalizeDate,
} from "../../utils/NormalizationUtil.ts";
import { AnnexData } from "../../types/annex.ts";
import { POLICY_FULL_OBJECT } from "../../constants/policyFullObjectDefaultState.ts";
import { COMMON_API_URL } from "../../constants/urls";
import { DocumentPPPRequest } from "../../pages/DocumentsPPP/DocumentsPPP.tsx";

type KeyConversionType = "camelCase" | "topLevel" | "none";

interface FetchOptions {
  convertKeys?: KeyConversionType;
}

const fetchData = async (
  jwt: string,
  endpoint: string,
  body: any = {},
  options: FetchOptions = {}
): Promise<any> => {
  const { convertKeys = "topLevel" } = options;

  const response = await fetch(`${COMMON_API_URL}/${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  let data;

  switch (convertKeys) {
    case "camelCase":
      data = CapitalizationUtil.convertKeysToCamelCase(await response.json());
      break;
    case "none":
      data = await response.json();
      break;
    default:
      data = CapitalizationUtil.convertTopLevelKeysToCamelCase(
        await response.json()
      );
  }

  if (data.error || data.Error) {
    throw new Error(data.error || data.Error);
  }

  return data;
};

const fetchPolicies = async (
  jwt: string,
  formState: PoliciesFormState,
  callfunc: string
): Promise<any[]> => {
  const {
    fromDate,
    toDate,
    company = "all",
    policyType = "all",
    office = "all",
    agent = "all",
    identificationNumber = "",
  } = formState;

  const params = {
    StartDate: fromDate.split("/").reverse().join(""),
    EndDate: toDate.split("/").reverse().join(""),
    InsComp: company || "all",
    InsType: policyType || "all",
    Office: office || "all",
    Agent: agent || "all",
    PIN: identificationNumber || "",
  };

  const data = await fetchData(jwt, callfunc, params);
  return data.result;
};

const fetchAgentDelta = async (
  agentId: string,
  policyTypeId: number,
  insuranceCompanyId: string,
  jwt: string
): Promise<string> => {
  const data = await fetchData(jwt, "getDelta", {
    AID: agentId,
    PID: policyTypeId,
    CID: insuranceCompanyId,
  });

  return data.result;
};

const normalizePolicyDates = (policy: PolicyFullObject): PolicyFullObject => ({
  ...policy,
  startDate: normalizeDate(policy.startDate),
  endDate: normalizeDate(policy.endDate),
  reportDate: normalizeDate(policy.reportDate),
  contractDate: normalizeDate(policy.contractDate),
});

const normalizePolicyValuesWithDates = (value: object): PolicyValuesObject => {
  const normalizedValue = normalizePolicyValues(value);

  normalizedValue.installmentDate = normalizeDate(
    normalizedValue.installmentDate
  );

  normalizedValue.reportDate = normalizeDate(normalizedValue.reportDate);

  normalizedValue.bankPaymentDate = normalizeDate(
    normalizedValue.bankPaymentDate
  );

  return normalizedValue;
};

const fetchPolicyByPolicyNumber = async (
  policyNumber: string,
  jwt: string,
  insuranceCompany = "",
  insuranceType = 0
): Promise<PolicyFullObject> => {
  const data = await fetchData(jwt, "getPolicy", {
    polnum: policyNumber,
    inscomp: insuranceCompany,
    instype: insuranceType,
  });

  if (Object.values(data.result).length === 0) {
    return {
      ...POLICY_FULL_OBJECT,
      policyNumber: policyNumber,
    };
  }

  if (Object.values(data.result).length > 1) {
    throw new Error(
      `Има повече от една полица с № ${policyNumber}; Моля изберете ЗК и вид застраховка`
    );
  }

  const normalizedPolicy = normalizePolicyFullProperties(
    data.result.POLICY
  ) as PolicyFullObject;

  return normalizePolicyDates(normalizedPolicy);
};

const fetchValuesByPolicyId = async (policyId: string, jwt: string) => {
  const data = await fetchData(jwt, "getValiors", { POL_ID: policyId });

  return Object.values(data.result).map(normalizePolicyValuesWithDates);
};

const fetchPolicyDetailsByPolicyId = async (
  policyNumber: string,
  jwt: string
): Promise<PolicyDetailsData> => {
  const data = await fetchData(jwt, "getPol_by_ID", {
    POL_ID: policyNumber,
  });

  if (data.error) {
    throw new Error(`Грешка при зареждане на полица с номер ${policyNumber}`);
  }

  const normalizedPolicy = normalizePolicyFullProperties(
    data.result.Policy
  ) as PolicyFullObject;

  const normalizedPolicyValues: PolicyValuesObject[] = Object.values(
    data.result.Valiors ?? {}
  ).map((valior) =>
    normalizePolicyValuesWithDates(valior as Record<string, unknown>)
  );
  return {
    policy: normalizePolicyDates(normalizedPolicy),
    policyValues: normalizedPolicyValues,
  };
};

const fetchPayPlaces = async (
  jwt: string
): Promise<{ [key: string]: string }> => {
  const data = await fetchData(jwt, "getPayPlaces", {});
  return data.result;
};

const fetchStickersByCompanyId = async (
  companyId: string,
  jwt: string
): Promise<string[]> => {
  const data = await fetchData(jwt, "getStickers", { InsComp: companyId });
  return data.result;
};

const fetchGreenCardsByCompanyId = async (
  companyId: string,
  jwt: string
): Promise<string[]> => {
  const data = await fetchData(jwt, "getGreenCards", { InsComp: companyId });
  return data.result;
};

const fetchPaymentAccountsByCompanyId = async (
  companyId: string,
  jwt: string
): Promise<string[]> => {
  const data = await fetchData(jwt, "getSmetki", { InsComp: companyId });
  return data.result;
};

const savePolicyData = async (
  policy: PolicyFullObject,
  jwt: string
): Promise<{ message: string; policyId: string }> => {
  if (!policy.policyId) {
    policy.policyId = "0";
  }

  const normalizedPolicyWithDates = {
    ...policy,
    policyNumber: policy.policyNumber.trim(),
    policyStatus: policy.policyStatus == "0" ? "1" : policy.policyStatus,
    startDate: revertNormalizeDate(policy.startDate),
    endDate: revertNormalizeDate(policy.endDate),
    reportDate: revertNormalizeDate(policy.reportDate),
    contractDate: revertNormalizeDate(policy.contractDate),
  };

  const normalizedPolicy = normalizePolicySaveProperties(
    normalizedPolicyWithDates,
    true
  );

  const data = await fetchData(jwt, "Policy_write", {
    Policy: normalizedPolicy,
  });

  return { message: data.message, policyId: data.pOLID };
};

const savePolicyInstallments = async (
  policyId: string,
  installments: PolicyValuesObject[],
  jwt: string
): Promise<{ message: string; policyId: string }> => {
  if (!policyId) {
    policyId = "0";
  }

  const normalizedInstallmentsWithDates = Object.keys(installments).reduce(
    (acc, key) => {
      const installment = { ...installments[key] };
      if (installment.installmentDate) {
        installment.installmentDate = revertNormalizeDate(
          installment.installmentDate
        );
      }
      if (installment.reportDate) {
        installment.reportDate = revertNormalizeDate(installment.reportDate);
      }

      if (!installment.policyId) {
        installment.policyId = policyId ? policyId : 0;
      }

      acc[key] = installment;
      return acc;
    },
    {}
  );

  const normalizedInstallments: {} = Object.keys(
    normalizedInstallmentsWithDates
  ).reduce((acc, key) => {
    const installment = normalizedInstallmentsWithDates[key];
    acc[installment.installmentSequence] = normalizePolicyValues(
      installment,
      true
    ) as PolicyValuesObject;
    return acc;
  }, {});

  const data = await fetchData(jwt, "Valiors_write", {
    POL_ID: policyId,
    Valiors: normalizedInstallments,
  });

  return { message: data.message, policyId: data.pOLID };
};

const fetchAnnexDataById = async (
  annexId: number,
  jwt: string
): Promise<AnnexData> => {
  const data = await fetchData(jwt, "getAnnex", { ADD_ID: annexId });
  return normalizeAnnexValues(data.result.Annex);
};

const saveAnnexData = async (
  annexData: AnnexData,
  jwt: string
): Promise<string> => {
  const data = await fetchData(jwt, "Annex_write", { Annex: annexData });
  return data.message;
};

/** Serializes selectedOffices array to a comma-separated string for the API. */
const serializeOffices = (offices: string[]): string =>
  offices.length > 0 ? offices.join(",") : "";

const buildReportParams = (reportData: ReportData) => ({
  StartDate: reportData.fromDate,
  EndDate: reportData.toDate,
  InsComp: reportData.company || "all",
  InsType: reportData.policyType || "all",
  Select_Off: serializeOffices(reportData.selectedOffices),
  Agent: reportData.agent || "all",
});

const fetchInsuranceCompanyReport = async (
  reportData: ReportData,
  jwt: string
): Promise<object> => {
  const data = await fetchData(jwt, "getRpt_ZK", {
    ...buildReportParams(reportData),
    Payment: reportData.paymentPlace || "all",
    annulated: reportData.shouldIncludeAnnulledPolicies,
  });

  return data.result;
};

const fetchReportedInsurances = async (
  reportData: ReportData,
  jwt: string
): Promise<object> => {
  const data = await fetchData(jwt, "getReportedPolicies", {
    DocDate: reportData.fromDate,
    NumOt: reportData.reportNumber,
    InsComp: reportData.company,
    InsType: reportData.policyType || "all",
    Select_Off: serializeOffices(reportData.selectedOffices),
    Agent: reportData.agent || "all",
  });

  return data.result;
};

const formatDateForReport = (date: string) =>
  date.split("/").reverse().join("");

const fetchBrokerReport = async (
  reportData: ReportData,
  jwt: string
): Promise<object> => {
  const data = await fetchData(jwt, "getBrockerList", {
    StartDate: formatDateForReport(reportData.fromDate),
    EndDate: formatDateForReport(reportData.toDate),
    InsComp: reportData.company || "all",
    InsType: reportData.policyType || "all",
    Select_Off: serializeOffices(reportData.selectedOffices),
    Agent: reportData.agent || "all",
  });

  return data.result;
};

const fetchBankReport = async (
  reportData: ReportData,
  jwt: string
): Promise<BankReportData> => {
  const data = await fetchData(jwt, "getRpt_Bank", {
    StartDate: formatDateForReport(reportData.fromDate),
    EndDate: formatDateForReport(reportData.toDate),
    InsComp: reportData.company || "all",
    InsType: reportData.policyType || "all",
    Select_Off: serializeOffices(reportData.selectedOffices),
    Agent: reportData.agent || "all",
  });

  return data.result;
};

const saveBankReport = async (
  selectedPolicies: [policyId: string, paymentNumber: string, date: string][],
  jwt: string
): Promise<BankReportData> => {
  const data = await fetchData(jwt, "writeRpt_Bank", {
    Pol_Arr: selectedPolicies,
  });
  return data.result;
};

const fetchPOSReport = async (
  reportData: ReportData,
  jwt: string
): Promise<BankReportData> => {
  const data = await fetchData(jwt, "getRpt_POS", {
    StartDate: formatDateForReport(reportData.fromDate),
    EndDate: formatDateForReport(reportData.toDate),
    InsComp: reportData.company || "all",
    InsType: reportData.policyType || "all",
    Select_Off: serializeOffices(reportData.selectedOffices),
    AID: reportData.agent || "all",
  });

  return data.result;
};

const savePOSReport = async (
  selectedPolicies: [policyId: string, paymentNumber: string, date: string][],
  jwt: string
): Promise<BankReportData> => {
  const data = await fetchData(jwt, "writeRpt_POS", {
    Pol_Arr: selectedPolicies,
  });
  return data.result;
};

const fetchNoPaidReport = async (
  reportData: ReportData,
  jwt: string
): Promise<BankReportData> => {
  const data = await fetchData(jwt, "getRpt_NoPaid", {
    StartDate: formatDateForReport(reportData.fromDate),
    EndDate: formatDateForReport(reportData.toDate),
    InsComp: reportData.company || "all",
    InsType: reportData.policyType || "all",
    Select_Off: serializeOffices(reportData.selectedOffices),
    AID: reportData.agent || "all",
  });

  return data.result;
};

const saveNoPaidReport = async (
  selectedPolicies: [
    policyId: string,
    paymentNumber: string,
    date: string,
    paymentPlace: string
  ][],
  jwt: string
): Promise<BankReportData> => {
  const data = await fetchData(jwt, "writeRpt_NoPaid", {
    Pol_Arr: selectedPolicies,
  });
  return data.result;
};

const fetchAgentReport = async (
  reportData: ReportData,
  jwt: string
): Promise<object> => {
  const data = await fetchData(jwt, "getRpt_Agent", {
    StartDate: reportData.fromDate,
    EndDate: reportData.toDate,
    InsComp: reportData.company || "all",
    InsType: reportData.policyType || "all",
    Agent: reportData.agent,
  });

  return data.result;
};

const saveAgentReport = async (
  reportData: AgentReportData,
  jwt: string
): Promise<object> => {
  const data = await fetchData(jwt, "writeRpt_Agent", {
    Doc_Date: reportData.reportDate,
    Document: reportData.reportNumber,
    Agent: reportData.agent,
    Pol_Arr: reportData.policies,
  });

  return data.result;
};

const parseAllValue = (value?: string) => {
  return value === "0" || !value ? "all" : value;
};

const fetchDailyReport = async (
  reportData: ReportData,
  jwt: string
): Promise<object> => {
  const data = await fetchData(jwt, "getFinDay", {
    StartDate: formatDateForReport(reportData.fromDate),
    EndDate: formatDateForReport(reportData.toDate),

    InsComp: parseAllValue(reportData.company),

    InsType: parseAllValue(reportData.policyType),

    Payment:
      reportData.paymentPlaces?.length > 0
        ? reportData.paymentPlaces.map((p) => (p === "0" ? "all" : p)).join(",")
        : "all",

    Select_Off: serializeOffices(reportData.selectedOffices),

    Agent: parseAllValue(reportData.agent),
  });

  return data.result;
};

const fetchKFNOperationsReport = async (
  reportData: {
    fromDate: string;
    toDate: string;
    company: string;
    policyType: string;
    ext_pol: "0" | "1";
    nopay_pol: "0" | "1";
    sum_pol_0: "0" | "1";
  },
  jwt: string
): Promise<object[]> => {
  const data = await fetchData(
    jwt,
    "clearKFN",
    {
      StartDate: formatDateForReport(reportData.fromDate),
      EndDate: formatDateForReport(reportData.toDate),
      InsComp: reportData.company || "all",
      InsType: reportData.policyType || "all",
      ext_pol: reportData.ext_pol,
      nopay_pol: reportData.nopay_pol,
      sum_pol_0: reportData.sum_pol_0,
    },
    { convertKeys: "none" }
  );

  if (!data.Result) return [];

  return Object.values(data.Result);
};

const saveNewPolicy = async (
  newPolicy: NewPolicyDocumentObject,
  jwt: string
): Promise<string> => {
  const data = await fetchData(jwt, "newpol_write", {
    Action: newPolicy.action,
    StartValue: newPolicy.startValue,
    EndValue: newPolicy.endValue,
    Agent: newPolicy.agent,
    InsComp: newPolicy.insuranceCompany,
    InsType: newPolicy.policyType,
    ValDate: revertNormalizeDate(newPolicy.date),
  });

  return data.message;
};

const fetchDocument = async (
  documentNumber: string,
  insuranceCompany: string,
  insuranceType: string,
  documentType: number,
  jwt: string
): Promise<Array<object>> => {
  try {
    const data = await fetchData(jwt, "searchDocuments", {
      DocNum: documentNumber,
      InsComp: insuranceCompany || "all",
      InsType: insuranceType || "all",
      DocType: documentType,
    });

    if (data.result === null || data.result.length === 0) {
      throw new Error(`Няма намерен документ с номер ${documentNumber}`);
    }

    return data.result;
  } catch (error) {
    throw new Error(`Грешка при търсене на документ`);
  }
};

const fetchDocumentLog = async (
  documentNumber: string,
  insuranceCompany: string,
  insuranceType: string,
  documentType: string,
  jwt: string
): Promise<Array<object>> => {
  try {
    const data = await fetchData(jwt, "getDocLog", {
      DocNum: documentNumber,
      InsComp: insuranceCompany,
      InsType: insuranceType,
      DocType: documentType,
    });

    if (data.result.length === 0) {
      throw new Error(
        `Няма намерена активност за документ с номер ${documentNumber}`
      );
    }

    return data.result;
  } catch (error) {
    throw new Error(error.message);
  }
};

const saveNewDocument = async (
  newDocument: NewDocumentObject,
  jwt: string
): Promise<string> => {
  try {
    const data = await fetchData(jwt, "newdoc_write", {
      DocType: newDocument.documentType,
      Action: newDocument.action,
      StartValue: newDocument.startValue,
      EndValue: newDocument.endValue,
      Agent: newDocument.agent,
      InsComp: newDocument.insuranceCompany,
      ValDate: revertNormalizeDate(newDocument.date),
    });

    return data.message;
  } catch (error) {
    throw new Error(
      `Няма намерени номера от ${newDocument.startValue} до ${newDocument.endValue}`
    );
  }
};

const fetchEmptyDocuments = async (
  emptyDocumentFormStateObject: EmptyDocumentObject,
  jwt: string
): Promise<object> => {
  try {
    const data = await fetchData(jwt, "getEmptyDocs", {
      DocType: emptyDocumentFormStateObject.documentType,
      Agent: emptyDocumentFormStateObject.agent || "all",
      InsType: emptyDocumentFormStateObject.insuranceType || "all",
      Status: emptyDocumentFormStateObject.documentStatus,
      Office: emptyDocumentFormStateObject.office || "all",
      InsComp: emptyDocumentFormStateObject.insuranceCompany,
    });

    return data.result;
  } catch (error) {
    throw new Error(`Няма намерени празни документи`);
  }
};

const fetchKFNAuditReport = async (
  {
    startDate,
    endDate,
    extPol,
  }: { startDate: string; endDate: string; extPol: string },
  jwt: string
): Promise<{ url: string }> => {
  const data = await fetchData(
    jwt,
    "getKFN",
    {
      StartDate: startDate,
      EndDate: endDate,
      ext_pol: extPol,
    },
    { convertKeys: "none" }
  );

  if (data.Error) {
    throw new Error("Грешка при извличане на справката за КФН");
  }

  return data;
};

const fetchDocumentPPP = async (
  reportData: DocumentPPPRequest,
  pppStatus: "0" | "4" | "5",
  jwt: string
): Promise<object[]> => {
  const data = await fetchData(
    jwt,
    "ppp",
    {
      StartDate: formatDateForReport(reportData.fromDate),
      EndDate: formatDateForReport(reportData.toDate),

      InsComp: reportData.company || "all",
      InsType: reportData.policyType || "all",
      Agent: reportData.agent || "all",
      ppp_status: pppStatus,
    },
    { convertKeys: "none" }
  );

  if (!data.Result) {
    return [];
  }

  return Object.entries(data.Result)
    .filter(([key]) => key !== "Head")
    .map(([, value]) => value as object);
};

const PolicyService = {
  fetchPolicies,
  fetchPolicyByPolicyNumber,
  fetchPolicyDetailsByPolicyId,
  fetchValuesByPolicyId,
  fetchPayPlaces,
  fetchStickersByCompanyId,
  fetchGreenCardsByCompanyId,
  fetchPaymentAccountsByCompanyId,
  fetchAnnexDataById,
  fetchInsuranceCompanyReport,
  fetchReportedInsurances,
  fetchDocument,
  fetchEmptyDocuments,
  fetchDocumentLog,
  fetchBrokerReport,
  fetchDailyReport,
  fetchBankReport,
  fetchPOSReport,
  fetchNoPaidReport,
  fetchKFNAuditReport,
  fetchKFNOperationsReport,
  fetchAgentReport,
  fetchAgentDelta,
  fetchDocumentPPP,
  saveBankReport,
  savePOSReport,
  saveNoPaidReport,
  savePolicyData,
  saveNewPolicy,
  saveNewDocument,
  saveAnnexData,
  saveAgentReport,
  savePolicyInstallments,
};

export default PolicyService;
